/**
 * Grid display for profile items from the Listing block.
 *
 * Renders portrait, name, and badges. Works with catalog brains where
 * `title` is already computed.
 * @module components/Blocks/Listing/ProfilesGridTemplate
 */
import React from 'react';
import ConditionalLink from '@plone/volto/components/manage/ConditionalLink/ConditionalLink';
import ProfileItem from './ProfileItem';
import './profiles-grid-template.scss';

import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';

import type { LinkHref, ProfileBrain } from '../../../types';

interface ProfilesGridTemplateProps {
  /** The brains the Listing block's query returned. */
  items: ProfileBrain[];
  /** Label for the optional "more" link under the grid. */
  linkTitle?: string;
  /** Where that link points, as the object-browser widget stores it. */
  linkHref?: LinkHref;
  /** True while the block is being edited, when links must not navigate. */
  isEditMode?: boolean;
}

const ProfilesGridTemplate: React.FC<ProfilesGridTemplateProps> = ({
  items,
  linkTitle,
  linkHref,
  isEditMode,
}) => {
  let link = null;
  const href = linkHref?.[0]?.['@id'] || '';

  if (isInternalURL(href)) {
    link = (
      <ConditionalLink to={flattenToAppURL(href)} condition={!isEditMode}>
        {linkTitle || href}
      </ConditionalLink>
    );
  } else if (href) {
    link = <a href={href}>{linkTitle || href}</a>;
  }

  return (
    <>
      <div className="profiles grid items">
        {items.map((item) => (
          <ProfileItem item={item} isEditMode={isEditMode} key={item['@id']} />
        ))}
      </div>

      {link && <div className="footer">{link}</div>}
    </>
  );
};

export default ProfilesGridTemplate;
